import React, { useState, useRef, useEffect } from 'react';
import './MultiSelect.css';

interface Option {
  id: number | string;
  name: string;
}

interface MultiSelectProps {
  options: Option[];
  value: (number | string)[];
  onChange: (selected: (number | string)[]) => void;
  placeholder?: string;
  label?: string;
}

export const MultiSelect: React.FC<MultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder = 'Select options...',
  label
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
        setSearchQuery('');
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isOpen && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [isOpen]);

  // Filter options based on search query (prefix matching)
  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().startsWith(searchQuery.toLowerCase())
  );

  const handleToggle = (optionId: number | string) => {
    if (value.includes(optionId)) {
      onChange(value.filter(id => id !== optionId));
    } else {
      onChange([...value, optionId]);
    }
  };

  const handleSelectAll = () => {
    const filteredIds = filteredOptions.map(opt => opt.id);
    const allFilteredSelected = filteredIds.every(id => value.includes(id));

    if (allFilteredSelected) {
      // Deselect all filtered options
      onChange(value.filter(id => !filteredIds.includes(id)));
    } else {
      // Select all filtered options
      const newValue = [...value];
      filteredIds.forEach(id => {
        if (!newValue.includes(id)) {
          newValue.push(id);
        }
      });
      onChange(newValue);
    }
  };

  const getDisplayText = () => {
    if (value.length === 0) return placeholder;
    if (value.length === options.length) return 'All Selected';
    if (value.length === 1) {
      const selected = options.find(opt => opt.id === value[0]);
      return selected?.name || placeholder;
    }
    return `${value.length} selected`;
  };

  return (
    <div className="multiselect-container" ref={dropdownRef}>
      {label && <label className="multiselect-label">{label}</label>}
      <div
        className={`multiselect-trigger ${isOpen ? 'open' : ''}`}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className="multiselect-text">{getDisplayText()}</span>
        <span className="multiselect-arrow">▼</span>
      </div>

      {isOpen && (
        <div className="multiselect-dropdown">
          <div className="multiselect-search">
            <input
              ref={searchInputRef}
              type="text"
              className="multiselect-search-input"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onClick={(e) => e.stopPropagation()}
            />
          </div>
          <div className="multiselect-divider"></div>
          <div className="multiselect-option select-all" onClick={handleSelectAll}>
            <input
              type="checkbox"
              checked={filteredOptions.length > 0 && filteredOptions.every(opt => value.includes(opt.id))}
              readOnly
            />
            <span>Select All {searchQuery && `(${filteredOptions.length})`}</span>
          </div>
          <div className="multiselect-divider"></div>
          {filteredOptions.length > 0 ? (
            filteredOptions.map(option => (
              <div
                key={option.id}
                className="multiselect-option"
                onClick={() => handleToggle(option.id)}
              >
                <input
                  type="checkbox"
                  checked={value.includes(option.id)}
                  readOnly
                />
                <span>{option.name}</span>
              </div>
            ))
          ) : (
            <div className="multiselect-no-results">No results found</div>
          )}
        </div>
      )}
    </div>
  );
};
